import React, { useMemo } from 'react';
import { Form, Spin, Switch, Typography, notification } from 'antd';
import { InformationBox } from '@prio365/prio365-react-library';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { makePrioStyles } from '../../../theme/utils';
import { ProjectId } from '../../../models/Types';
import { ESettingScope, Setting } from '../../../models/Settings';
import {
  apiSaveSharedMailboxAutoMarkAsRead,
  SHARED_MAILBOX_AUTO_MARK_AS_READ_SETTING_KEY,
} from '../../settings/api';
import useSharedMailboxAutoMarkAsRead, {
  sharedMailboxAutoMarkAsReadQueryKeys,
} from '../../mail/hooks/useSharedMailboxAutoMarkAsRead';
import { useAccessRights } from '../../users/hooks/useAccessRights';
import useProjectsContext from '../hooks/useProjectsContext';

const useStyles = makePrioStyles((theme) => ({
  root: {
    backgroundColor: theme.old.palette.backgroundPalette.content,
    padding: theme.old.spacing.defaultPadding,
  },
  header: {
    marginBottom: theme.old.spacing.unit(1.5),
  },
  source: {
    fontSize: theme.old.typography.fontSize.small,
    color: theme.old.typography.colors.muted,
  },
}));

interface ProjectMailAutoMarkAsReadSettingProps {
  projectId: ProjectId;
  className?: string;
}

export const ProjectMailAutoMarkAsReadSetting: React.FC<
  ProjectMailAutoMarkAsReadSettingProps
> = (props) => {
  //#region ------------------------------ Defaults
  const { projectId, className } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const queryKey = sharedMailboxAutoMarkAsReadQueryKeys.byProject(projectId);

  const { getProjectById } = useProjectsContext();
  const project = useMemo(
    () => getProjectById(projectId),
    [getProjectById, projectId]
  );

  const accessRightsSearchItems = useMemo(
    () => ({ projectId, officeId: project?.internalOfficeId }),
    [projectId, project?.internalOfficeId]
  );
  const accessRights = useAccessRights(
    ['editProjectMailAutoMarkAsRead'],
    accessRightsSearchItems,
    true
  );
  const canEdit = accessRights.editProjectMailAutoMarkAsRead;
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const { setting, isEnabled, isSettingLoading } =
    useSharedMailboxAutoMarkAsRead(projectId);

  const sourceLabel = useMemo(() => {
    if (setting?.scope === ESettingScope.PROJECT) {
      return t('projects:projectMailAutoMarkAsReadSetting.source.project');
    }
    if (setting?.jsonValue && setting.scope === ESettingScope.OFFICE) {
      return t('projects:projectMailAutoMarkAsReadSetting.source.office');
    }
    if (setting?.jsonValue && setting.scope === ESettingScope.GLOBAL) {
      return t('projects:projectMailAutoMarkAsReadSetting.source.global');
    }
    return t('projects:projectMailAutoMarkAsReadSetting.source.default');
  }, [setting, t]);
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const saveMutation = useMutation({
    mutationFn: async (nextIsEnabled: boolean) => {
      const { result, data } = await apiSaveSharedMailboxAutoMarkAsRead(
        projectId,
        nextIsEnabled,
        setting
      );
      if (result.status < 200 || result.status >= 300) {
        throw new Error(result.statusText);
      }
      return data;
    },
    onMutate: async (nextIsEnabled) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<Setting | null>(queryKey);
      queryClient.setQueryData<Setting | null>(queryKey, (current) => ({
        parentSettingId: '',
        relationId: '',
        couldNotBeLoadedReason: '',
        ...current,
        settingId:
          current?.scope === ESettingScope.PROJECT ? current.settingId : '',
        key: SHARED_MAILBOX_AUTO_MARK_AS_READ_SETTING_KEY,
        scope: ESettingScope.PROJECT,
        scopeId: projectId,
        isDefault: true,
        couldNotBeLoaded: false,
        jsonValue: JSON.stringify({ isEnabled: nextIsEnabled }),
      }));
      return { previous };
    },
    onError: (_error, _nextIsEnabled, context) => {
      queryClient.setQueryData(queryKey, context?.previous ?? null);
      notification.open({
        message: t('common:error'),
        description: t(
          'projects:projectMailAutoMarkAsReadSetting.notification.saveError'
        ),
      });
    },
    onSuccess: (savedSetting) => {
      if (savedSetting) {
        queryClient.setQueryData<Setting | null>(queryKey, savedSetting);
      }
      queryClient.invalidateQueries({
        queryKey: sharedMailboxAutoMarkAsReadQueryKeys.all,
      });
    },
  });
  //#endregion

  return (
    <div className={classNames(classes.root, className)}>
      <div className={classes.header}>
        <Typography.Title>
          {t('projects:projectMailAutoMarkAsReadSetting.headline')}
        </Typography.Title>
        <InformationBox
          description={t(
            'projects:projectMailAutoMarkAsReadSetting.description'
          )}
          marginBottom="regular"
        />
      </div>
      {isSettingLoading ? (
        <Spin size="small" />
      ) : (
        <Form layout="vertical">
          <Form.Item label={sourceLabel}>
            <Switch
              checked={isEnabled}
              loading={saveMutation.isLoading}
              disabled={!canEdit || saveMutation.isLoading}
              onChange={(checked) => saveMutation.mutate(checked)}
            />
          </Form.Item>
        </Form>
      )}
    </div>
  );
};

export default ProjectMailAutoMarkAsReadSetting;
